import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Badge } from '@/shared/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { SensorCard } from '@/modules/ezparcel/components/coldchain/SensorCard';
import {
  Radio,
  Plus,
  Search,
  Filter,
  Wifi,
  WifiOff,
  AlertTriangle,
  Battery,
  RefreshCw } from
'lucide-react';
import { toast } from 'sonner';

export const SensorManagement = () => {const { t } = useTranslation("ezparcel");
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [sensors, setSensors] = useState([]);
  const [newSensor, setNewSensor] = useState({
    name: '',
    serialNumber: '',
    type: '',
    minTemp: '',
    maxTemp: '',
    location: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const filteredSensors = sensors.filter((sensor) => {
    if (filterStatus !== 'all' && sensor.status !== filterStatus) return false;
    if (filterType !== 'all' && sensor.type !== filterType) return false;
    if (searchQuery && !sensor.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !sensor.serialNumber.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const statusCounts = {
    active: sensors.filter((s) => s.status === 'Active').length,
    warning: sensors.filter((s) => s.status === 'Warning').length,
    offline: sensors.filter((s) => s.status === 'Offline').length
  };

  const handleAddSensor = async () => {
    if (!newSensor.name || !newSensor.serialNumber || !newSensor.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800));

    const sensor = {
      id: `SEN-${String(sensors.length + 1).padStart(3, '0')}`,
      name: newSensor.name,
      serialNumber: newSensor.serialNumber,
      type: newSensor.type,
      status: 'Active',
      currentTemp: 4.5,
      minTemp: parseFloat(newSensor.minTemp) || 2,
      maxTemp: parseFloat(newSensor.maxTemp) || 8,
      batteryLevel: 100,
      signalStrength: 95,
      lastReading: new Date().toISOString(),
      location: newSensor.location || 'Unassigned',
      calibrationDate: new Date().toISOString().split('T')[0]
    };

    setSensors([sensor, ...sensors]);
    setNewSensor({ name: '', serialNumber: '', type: '', minTemp: '', maxTemp: '', location: '' });
    setIsAddDialogOpen(false);
    setIsLoading(false);
    toast.success(`Sensor "${newSensor.name}" added successfully!`);
  };

  return (
    <div className="space-y-6" data-testid="coldchain-sensors">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Radio className="h-8 w-8 text-cyan-600" />{t("sensorManagement", "Sensor Management")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageTemperatureSensors", "Manage temperature sensors, calibration, and device assignments")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("syncAll", "Sync All")}

          </Button>
          <Button
            className="gap-2 bg-cyan-600 hover:bg-cyan-700"
            onClick={() => setIsAddDialogOpen(true)}
            data-testid="add-sensor-btn">
            
            <Plus className="h-4 w-4" />{t("addSensor", "Add Sensor")}

          </Button>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-muted">
              <Radio className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-bold">{sensors.length}</p>
              <p className="text-sm text-muted-foreground">{t("totalSensors", "Total Sensors")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-emerald-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-100">
              <Wifi className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{statusCounts.active}</p>
              <p className="text-sm text-muted-foreground">{t("active", "Active")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-amber-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{statusCounts.warning}</p>
              <p className="text-sm text-muted-foreground">{t("warning", "Warning")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-gray-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gray-100">
              <WifiOff className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-600">{statusCounts.offline}</p>
              <p className="text-sm text-muted-foreground">{t("offline", "Offline")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or serial number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} />
              
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                <SelectItem value="Active">{t("active", "Active")}</SelectItem>
                <SelectItem value="Warning">{t("warning", "Warning")}</SelectItem>
                <SelectItem value="Offline">{t("offline", "Offline")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allTypes", "All Types")}</SelectItem>
                <SelectItem value="BLE">{t("ble", "BLE")}</SelectItem>
                <SelectItem value="Cellular">{t("cellular", "Cellular")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sensor Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredSensors.map((sensor) =>
        <SensorCard
          key={sensor.id}
          sensor={sensor}
          onBind={(s) => console.log('Bind sensor:', s.id)}
          onConfigure={(s) => console.log('Configure sensor:', s.id)} />

        )}
      </div>

      {filteredSensors.length === 0 &&
      <Card className="card-elevated">
          <CardContent className="p-8 text-center">
            <Radio className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t("noSensorsFound", "No sensors found")}</p>
            <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your filters")}</p>
          </CardContent>
        </Card>
      }

      {/* Low Battery Alert */}
      {sensors.filter((s) => s.batteryLevel < 20).length > 0 &&
      <Card className="card-elevated border-amber-300 bg-amber-50 dark:bg-amber-950/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Battery className="h-5 w-5" />{t("lowBatteryWarning", "Low Battery Warning")}

          </CardTitle>
            <CardDescription className="text-amber-600">{t("theFollowingSensors", "The following sensors have low battery and need attention")}

          </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {sensors.filter((s) => s.batteryLevel < 20).map((sensor) =>
            <Badge key={sensor.id} variant="outline" className="bg-white">
                  {sensor.serialNumber} - {sensor.batteryLevel}%
                </Badge>
            )}
            </div>
          </CardContent>
        </Card>
      }

      {/* Add Sensor Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio className="h-5 w-5 text-cyan-600" />{t("addNewSensor", "Add New Sensor")}

            </DialogTitle>
            <DialogDescription>{t("registerANew", "Register a new temperature sensor for cold chain monitoring.")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="sensor-name">{t("sensorName", "Sensor Name *")}</Label>
                <Input
                  id="sensor-name"
                  placeholder="Warehouse Sensor 01"
                  value={newSensor.name}
                  onChange={(e) => setNewSensor({ ...newSensor, name: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="sensor-serial">{t("serialNumber", "Serial Number *")}</Label>
                <Input
                  id="sensor-serial"
                  placeholder="TMP-001-A1B2C3"
                  value={newSensor.serialNumber}
                  onChange={(e) => setNewSensor({ ...newSensor, serialNumber: e.target.value })} />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="sensor-type">{t("sensorType", "Sensor Type *")}</Label>
                <Select
                  value={newSensor.type}
                  onValueChange={(value) => setNewSensor({ ...newSensor, type: value })}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BLE">{t("bleBluetooth", "BLE (Bluetooth)")}</SelectItem>
                    <SelectItem value="Cellular">{t("cellular", "Cellular")}</SelectItem>
                    <SelectItem value="WiFi">{t("wifi", "WiFi")}</SelectItem>
                    <SelectItem value="LoRa">{t("lora", "LoRa")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="sensor-location">{t("location", "Location")}</Label>
                <Input
                  id="sensor-location"
                  placeholder="Cold Room A"
                  value={newSensor.location}
                  onChange={(e) => setNewSensor({ ...newSensor, location: e.target.value })} />
                
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="sensor-min-temp">{t("minTemperatureC", "Min Temperature (\xB0C)")}</Label>
                <Input
                  id="sensor-min-temp"
                  type="number"
                  placeholder="2"
                  value={newSensor.minTemp}
                  onChange={(e) => setNewSensor({ ...newSensor, minTemp: e.target.value })} />
                
              </div>
              <div className="grid gap-2">
                <Label htmlFor="sensor-max-temp">{t("maxTemperatureC", "Max Temperature (\xB0C)")}</Label>
                <Input
                  id="sensor-max-temp"
                  type="number"
                  placeholder="8"
                  value={newSensor.maxTemp}
                  onChange={(e) => setNewSensor({ ...newSensor, maxTemp: e.target.value })} />
                
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleAddSensor}
              disabled={isLoading}
              className="bg-cyan-600 hover:bg-cyan-700">
              
              {isLoading ? 'Adding...' : 'Add Sensor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};